import React, { useState } from "react";
import useAuth from "../../auth/useAuth";
import { supplementOptimizerService } from "./optimizerService";
import SupplementAnalysisResults from "./optimizerResults";
import SupplementOptimizerHelp from "./optimizerHelp";
import HelpIcon from '@mui/icons-material/Help';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { FormControlLabel, Checkbox } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from '@mui/icons-material/AddCircle';

const SupplementOptimizer = () => {
  const { isAuthenticated, redirectToAuth, loading } = useAuth();
  const [inputMode, setInputMode] = useState("single");
  const [supplementText, setSupplementText] = useState("");
  const [supplementList, setSupplementList] = useState([{ name: "", dosage: "", note: "" }]);
  const [apiResponse, setApiResponse] = useState(null);
  const [buttonText, setButtonText] = useState("Optimize Supplements");
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const handleInputChange = (event) => {
    setSupplementText(event.target.value);
  };

  const handleModeChange = (event) => {
    setInputMode(event.target.checked ? "bulk" : "single");
  };

  const handleListChange = (index, field, value) => {
    const updatedList = [...supplementList];
    updatedList[index][field] = value;
    setSupplementList(updatedList);
  };

  const addRow = () => {
    setSupplementList([...supplementList, { name: "", dosage: "", note: "" }]);
  };

  const removeRow = (index) => {
    if (supplementList.length > 1) {
      const updatedList = supplementList.filter((_, i) => i !== index);
      setSupplementList(updatedList);
    }
  };

  const parseSupplements = () => {
    if (inputMode === "bulk") {
      const warnings = [];
      const parsed = supplementText.split("\n").map((line, index) => {
        // Skip empty lines
        if (!line.trim()) return null;
        
        const parts = line.split(",").map(part => part.trim());
        const lineNumber = index + 1;
        
        // Check if line has required commas
        if (parts.length < 2) {
          warnings.push(`Line ${lineNumber}: Missing comma separator - "${line}"`);
        }
  
        // Still try to parse the line if possible
        return {
          name: parts[0] || "",
          dosage: parts[1] || "",
          note: parts[2] || "",
          hasError: parts.length < 2
        };
      }).filter(item => item !== null);
  
      // Display warnings if any
      if (warnings.length > 0) {
        console.warn("Parsing Warnings:");
        warnings.forEach(warning => console.warn(warning));
        // You could also set these warnings in your component state
        // setParsingWarnings(warnings);
      }
  
      return parsed;
    } else {
      return supplementList.filter(supp => supp.name && supp.dosage);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isAuthenticated) {
      console.warn("User not authenticated. Redirecting...");
      redirectToAuth();
      return;
    }
    const parsedSupplements = parseSupplements();
    if (parsedSupplements.length === 0) {
      console.error("No valid supplements found.");
      return;
    }
    try {
      const response = await supplementOptimizerService(parsedSupplements, setButtonText, setButtonDisabled);
      setApiResponse(response);
    } catch (error) {
      console.error(error);
    }
  };

  const handleHelpOpen = () => setHelpOpen(true);
  const handleHelpClose = () => setHelpOpen(false);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="section" id="supplementOptimizer">
      <h2>Supplement Optimizer</h2>
      <div className="list-section" style={{ position: 'relative', padding: '20px' }}>
        <IconButton onClick={handleHelpOpen} aria-label="help" style={{ position: "absolute", top: 5, right: 5, color: "#67761F" }}>
          <HelpIcon />
        </IconButton>
        <div className="list-container">
          <p>Want to optimize your supplement intake? Choose an input method below.</p>
          <FormControlLabel
            control={
              <Checkbox
                checked={inputMode === "bulk"}
                onChange={handleModeChange}
              />
            }
            label="Bulk Mode"
            sx={{ color: '#667761', display: 'block' }}
          />

          {inputMode === "bulk" ? (
            <div>
              <textarea
                value={supplementText}
                onChange={handleInputChange}
                placeholder={`Enter supplements, one per line in a comma delimited format:\nSupplement Name, Dosage, Notes \nPro Health Resveratrol, 1000mg, Take in the morning`}
                required
                rows={5}
                style={{ 
                  width: '100%', 
                  marginBottom: '10px',
                  padding: '5px',
                  borderRadius: '4px',
                  border: '1px solid #667761'
                }}
              />
              <button 
                className="btn-primary" 
                type="submit" 
                disabled={buttonDisabled} 
                onClick={handleSubmit}
                style={{ width: '100%', padding: '10px', marginTop: '10px' }}
              >
                {buttonText}
              </button>
            </div>
          ) : (
            <div >
              {supplementList.map((supp, index) => (
                <div className="list-container-input" key={index} style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
                    <input
                        type="text"
                        className="basic-input"
                        // label="Supplement Name" 
                        value={supp.name} 
                        placeholder="Supplement Name"
                        onChange={(e) => handleListChange(index, "name", e.target.value)}
                    />
                    <input
                        type="text"
                        className="basic-input"
                        placeholder="Dosage" 
                        value={supp.dosage} 
                        onChange={(e) => handleListChange(index, "dosage", e.target.value)}
                    />
                    <input
                        type="text"
                        className="basic-input"
                        placeholder="Note" 
                        value={supp.note} 
                        onChange={(e) => handleListChange(index, "note", e.target.value)}
                    />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Tooltip title="Add Row">
                      <AddCircleIcon onClick={addRow} style={{ color: '#667761', cursor: 'pointer' }} />
                    </Tooltip>
                    <Tooltip title="Remove Row">
                      <DeleteIcon 
                        onClick={() => removeRow(index)} 
                        style={{ 
                          color: supplementList.length > 1 ? '#667761' : '#ccc',
                          cursor: supplementList.length > 1 ? 'pointer' : 'not-allowed'
                        }} 
                      />
                    </Tooltip>
                  </div>
                </div>
              ))}
              <button 
                className="btn-primary" 
                type="submit" 
                disabled={buttonDisabled} 
                onClick={handleSubmit}
                style={{ width: '100%', padding: '10px', marginTop: '10px' }}
              >
                {buttonText}
              </button>
            </div>
          )}
        </div>
      </div>
      {apiResponse && <SupplementAnalysisResults response={apiResponse} />}
      <Dialog open={helpOpen} onClose={handleHelpClose} fullWidth maxWidth="md">
        <DialogTitle>
          Help - Supplement Optimizer User Manual
          <IconButton aria-label="close" onClick={handleHelpClose} style={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <SupplementOptimizerHelp />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplementOptimizer;
